import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Testimonials from "../../components/Testimonial";
import { AllPrograms } from "../../interfaces/Program";
import { programsDetailsConfig } from "../../SliderProperties/ProgramConfig";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  fetchDataProgramDetails,
  fetchProgramsFilter,
} from "../../store/ProgramSlice";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Sessions from "../../components/Sessions";
import { useNavigate, useParams } from "react-router-dom";
import ReadMoreComponent from "../../components/ReadMore";
import ProgramMinimalCard from "../../components/ProgramMinimalCard";
import { ISession } from "../../interfaces/Session";
import { createPaymentFree } from "../../store/paymentSlice";
const limit = 10;

const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve) => {
    if (!src) {
      resolve();
      return;
    }
    const img = new Image();
    img.src = src;
    if (img.complete) {
      resolve();
      return;
    }
    img.onload = () => resolve();
    img.onerror = () => resolve();
  });
};

export default function Program() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "fr" ? "fr-FR" : "en-US";
  const dispatch = useDispatch<any>();
  const { programSlug } = useParams<{ programSlug: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (programSlug) {
      setLoading(true);
      setImageLoaded(false);
      dispatch(fetchProgramsFilter({ programSlug, limit }));
      dispatch(fetchDataProgramDetails(programSlug))
        .then(async (action: any) => {
          const iconUrl = action?.payload?.icon;
          if (iconUrl) {
            await preloadImage(iconUrl);
          }
        })
        .catch(() => {})
        .finally(() => {
          setImageLoaded(true);
          setLoading(false);
        });
    }
  }, [dispatch, programSlug]);

  const programData = useSelector((state: RootState) => {
    return state.programs.data1;
  });

  const isDataLoading =
    loading ||
    !imageLoaded ||
    !programData ||
    (programData.slug && programData.slug !== programSlug);

  const formattedStartDate =
    programData?.startDate &&
    new Date(programData.startDate).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  const sessionData = programData?.sessions;
  const sessions: ISession[] | undefined = sessionData;
  const isBooked = programData?.isBooked;

  const programsAll = useSelector((state: RootState) => {
    return state.programs.data || ([] as AllPrograms[]);
  });

  const handleClick = () => {
    navigate('/checkout', { state: { programid: programData?.id, title: programData?.title, icon: programData?.icon, cost: programData?.cost, date: formattedStartDate } });
  };

  const handleFreeClick = () => {
    dispatch(createPaymentFree({ programId: programData?.id })).then(() => {
      //closePopup();
      navigate('/thankyou');
    });

  };

  return (
    <>
      <div className="relative">
        <div
          className="bg-cover bg-center text-center text-white p-8 "
          style={{
            backgroundImage: `url("./images/innerb.jpg")`,
          }}
        >
          <div className="text-center pt-40 pb-28">
            <h1 className="text-2xl md:text-4xl font-bold">{t("programDetail.title", "Program Details")}</h1>
          </div>
        </div>
      </div>

      {isDataLoading ? (
        <div className="container mx-auto my-24 min-h-[400px] flex flex-col items-center justify-center">
          <div className="w-14 h-14 border-4 border-[#037897]/20 border-t-[#037897] rounded-full animate-spin mb-4"></div>
          <p className="text-[#546E7A] text-lg font-medium animate-pulse">
            {t("programDetail.loading", "Loading program...")}
          </p>
        </div>
      ) : (
        <>
          <div className="container flex flex-col md:flex-row mx-auto my-10 items-center justify-center ">
            <div className=" md:basis-1/2">
              <h2 className="font-semibold text-2xl md:text-4xl capitalize text-[#020220] tracking-wide mt-2 mb-2 ml-4 md:hidden">
                {programData?.title}
              </h2>
              <img
                src={programData?.icon}
                alt={programData?.title || "Program image"}
                className="p-4 w-full h-auto object-cover"
                onLoad={() => setImageLoaded(true)}
              />
            </div>

            <div className="basis-full md:basis-1/2 p-6">
              <h2 className="font-semibold text-2xl md:text-4xl capitalize text-[#020220] tracking-wide mt-2 mb-8 hidden md:block">
                {programData?.title}
              </h2>
              <p className="mb-4 text-[#546E7A]">
                <ReadMoreComponent
                  text={programData?.description ?? ""}
                  maxLength={300}
                />
              </p>
              <div className="flex  gap-6">
                <div className="rounded-lg basis-6/12 shadow-[0_4px_6px_4px_rgb(0,0,0,0.1),0_2px_4px_-2px_rgb(0,0,0,0.1)] p-4 mb-6">
                  <p className="text-[#607D8B] mb-2">{t("programs.date", "Date")}</p>
                  <p className="text-xl">{formattedStartDate}</p>
                </div>
                <div className="rounded-lg basis-6/12 shadow-[0_4px_6px_4px_rgb(0,0,0,0.1),0_2px_4px_-2px_rgb(0,0,0,0.1)] p-4 mb-6">
                  <p className="text-[#607D8B] mb-2">{t("programs.totalSlots", "Total Slots")}</p>
                  <p className="text-xl">{(programData?.maxParticipants && programData?.maxParticipants < 9999) ? (
                    programData?.bookedSlots ? `${programData?.bookedSlots} / ${programData?.maxParticipants}` : `0 / ${programData?.maxParticipants}`
                  ) : t("programs.noLimits", "No limits")}</p>

                </div>
              </div>

              <div className="my-8">
                <p className="text-4xl font-bold text-[#037897]">
                  CHF {programData?.cost}
                </p>
              </div>
              {
                typeof programData?.cost === 'number' && programData?.cost === 0 ? (
                  !isBooked ? (
                    <button
                      className="uppercase bg-[#037897] text-white py-4 leading-3 font-bold rounded-md px-8"
                      onClick={() => {
                        handleFreeClick();
                      }}
                    >
                      {t("programDetail.bookFree", "Book for Free")}
                    </button>
                  ) : (
                    <p className="text-[#e11d48]">{t("programDetail.alreadyBooked", "You have already booked this program.")}</p>
                  )
                ) : (
                  programData?.bookedSlots !== undefined &&
                    programData?.maxParticipants !== undefined ? (
                    (programData?.bookedSlots < programData?.maxParticipants || programData?.maxParticipants === 9999) ? (
                      !isBooked ? (
                        <button
                          className="uppercase bg-[#037897] text-white py-4 leading-3 font-bold rounded-md px-8"
                          onClick={() => {
                            handleClick();
                          }}
                        >
                          {t("programs.bookNow", "Book Now")}
                        </button>
                      ) : (
                        <p className="text-[#e11d48]">{t("programDetail.alreadyBooked", "You have already booked this program.")}</p>
                      )
                    ) : (
                      <p className="text-[#e11d48]">{t("programDetail.programFull", "This Program is Full.")}</p>
                    )
                  ) : null
                )
              }

            </div>
          </div>
          {/* sessions section */}
          {sessions && sessions.length > 0 && <Sessions sessionData={sessions} />}
        </>
      )}

      <div
        style={{ backgroundImage: `url("./images/bg-d.jpg")` }}
        className="bg-cover bg-no-repeat relative p-2 pb-8"
      >
        <div className="container  md:mx-auto my-10 items-center justify-center ">
          <h4 className="text-center text-lg text-[#fff] capitalize mb-4 pt-10">
            {t("programDetail.latestPrograms", "Latest Programs")}
          </h4>
          <h2 className="text-center font-semibold text-2xl md:text-4xl capitalize text-white tracking-wide mb-10">
            {t("programDetail.otherPrograms", "You may also like other programs")}
          </h2>
          {programsAll.length > 0 && (
            <div className="mb-16">
              <Slider {...programsDetailsConfig}>
                {programsAll.map((programs: AllPrograms) => (
                  <ProgramMinimalCard key={programs.id} program={programs} />
                ))}
              </Slider>
            </div>
          )}
        </div>
        <div className=" absolute bottom-0 left-0">
          <img
            src="./images/pd-footer.png"
            className="m-auto mt-4"
          ></img>
        </div>
      </div>
      <div className="-m-9"></div>
      <Testimonials />
    </>
  );
}
